import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  DataError,
  LoadingIndicator,
  useAlert
} from "@digital-services/ui";

import ApplicationDropDown from "@organization/components/applicationAssociation/ApplicationDropDown";
import { useAssociateApplication } from "@organization/services/use-associate-organization-application";
import { useGetOrganization } from "@organization/services/use-get-organization";

interface Props {
  organizationId: string;
  onCancel: () => void;
  onSave: () => void;
}

export const AssociateApplicationForm = (props: Props) => {
  const { organizationId, onCancel, onSave } = props;
  const { organization, isLoading, error } = useGetOrganization(organizationId);

  const { t: tCommon } = useTranslation();
  const { t } = useTranslation(undefined, {
    keyPrefix: "organization.applications"
  });
  const { t: tApplication } = useTranslation(undefined, {
    keyPrefix: "organization.application"
  });

  const [applicationId, setApplicationId] = useState<string>("");
  const [isValidApplicationId, setIsValidApplicationId] =
    useState<boolean>(false);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const { associate } = useAssociateApplication();
  const { notifySuccess, notifyError } = useAlert();

  function saveChanges(): void {
    setIsBusy(true);
    associate(applicationId, organizationId)
      .then(response => {
        setIsBusy(false);
        if (response instanceof Error) {
          notifyError({
            header: t("associate.error.message"),
            "data-testid": "associate-application-error-alert"
          });
        } else {
          onSave();
          notifySuccess({
            header: t("associate.success.message"),
            "data-testid": "associate-application-success-alert"
          });
        }
      })
      .catch(() => {
        setIsBusy(false);
        notifyError({
          header: t("associate.error.message"),
          "data-testid": "associate-application-error-alert"
        });
      });
  }

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={t("load.message")} />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <DataError
        title={t("load.error.message")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  function handleApplicationChange(newValue: string, isValid: boolean): void {
    setApplicationId(newValue);
    setIsValidApplicationId(isValid);
  }

  return (
    <div
      className="flex flex-col gap-10"
      data-testid="associate-application-form"
    >
      <ApplicationDropDown
        label={tApplication("name.label")}
        organizationId={organizationId}
        selectedApplicationId={applicationId}
        onInputChange={handleApplicationChange}
      />
      <div className="flex flex-row-reverse gap-4">
        <Button
          data-testid="save"
          variant="primary"
          isLoading={isBusy}
          onClick={saveChanges}
          disabled={!isValidApplicationId}
        >
          {tCommon("button.save.label")}
        </Button>
        <Button data-testid="cancel" onClick={onCancel}>
          {tCommon("button.cancel.label")}
        </Button>
      </div>
    </div>
  );
};
